import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import { ArrowLeft, User, Mail, Lock, Shield } from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";

import {
  PageHeader,
  Card,
  Input,
  Select,
  FormField,
  Alert,
  Button,
  Spinner,
} from "../../design-system";

class UserForm extends Component {
  constructor(props) {
    super(props);
    const { id } = this.props.match.params;
    this.state = {
      id: id || null,
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "USER",
      loading: false,
      saving: false,
      errors: {},
    };
  }

  componentDidMount() {
    const token = localStorage.getItem("jwtToken");
    if (token) setAuthToken(token);
    if (this.state.id) this.loadUser();
  }

  loadUser = async () => {
    try {
      this.setState({ loading: true });
      const response = await axios.get(`/api/users/${this.state.id}`);
      if (response.data.success) {
        const user = response.data.user;
        this.setState({
          name: user.name || "",
          email: user.email || "",
          role: user.role || "USER",
          loading: false,
        });
      }
    } catch (error) {
      console.error("Error loading user:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur", message: "Impossible de charger l'utilisateur",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      this.props.history.push("/dashboard/users");
    }
  };

  handleChange = (e) => {
    this.setState({ [e.target.name]: e.target.value, errors: {} });
  };

  validate = () => {
    const errors = {};
    const { name, email, password, confirmPassword, id } = this.state;

    if (!name || name.trim().length < 2)
      errors.name = "Le nom doit contenir au moins 2 caractères";
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errors.email = "L'email n'est pas valide";

    if (!id) {
      if (!password || password.length < 6)
        errors.password = "Le mot de passe doit contenir au moins 6 caractères";
      if (password !== confirmPassword)
        errors.confirmPassword = "Les mots de passe ne correspondent pas";
    } else {
      if (password && password.length > 0 && password.length < 6)
        errors.password = "Le mot de passe doit contenir au moins 6 caractères";
      if (password && password !== confirmPassword)
        errors.confirmPassword = "Les mots de passe ne correspondent pas";
    }
    return errors;
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }
    try {
      this.setState({ saving: true });
      const { id, name, email, password, role } = this.state;
      const data = { name, email, role };
      if (password && password.trim() !== "") data.password = password;

      const response = id
        ? await axios.put(`/api/users/${id}`, data)
        : await axios.post("/api/users", data);

      if (response.data.success) {
        Store.addNotification({
          title: id ? "Utilisateur mis à jour" : "Utilisateur créé",
          message: `« ${name} » enregistré.`,
          type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
        });
        this.props.history.push("/dashboard/users");
      }
    } catch (error) {
      console.error("Error saving user:", error);
      const errorData = error.response?.data || {};
      this.setState({
        errors: errorData.errors || { general: errorData.message || "Erreur lors de l'enregistrement" },
      });
      Store.addNotification({
        title: "Erreur",
        message: errorData.message || "Erreur lors de l'enregistrement",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const { id, loading, saving, errors } = this.state;
    const isEdit = !!id;
    const pageTitle = isEdit ? "Modifier l'utilisateur" : "Nouvel utilisateur";

    if (loading) {
      return (
        <AdminPageShell pageTitle={pageTitle}>
          <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
        </AdminPageShell>
      );
    }

    return (
      <AdminPageShell pageTitle={pageTitle} breadcrumbLast={isEdit ? (this.state.email || this.state.name || "Modifier") : undefined}>
        <PageHeader
          eyebrow="— Utilisateurs"
          title={pageTitle}
          description={isEdit ? "Mets à jour les informations de ce compte." : "Crée un nouveau compte avec accès à l'admin."}
          actions={
            <Button
              variant="secondary"
              leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={1.8} />}
              onClick={() => this.props.history.push("/dashboard/users")}
            >
              Retour
            </Button>
          }
        />

        <form onSubmit={this.handleSubmit}>
          <div className="max-w-2xl space-y-5">
            {errors.general && (
              <Alert variant="danger">{errors.general}</Alert>
            )}

            <Card>
              <Card.Header><Card.Title>Identité</Card.Title></Card.Header>
              <div className="space-y-4">
                <FormField label="Nom" required error={errors.name} hint={<User className="w-3.5 h-3.5" />}>
                  <Input
                    name="name"
                    value={this.state.name}
                    onChange={this.handleChange}
                    placeholder="Karim"
                    required
                  />
                </FormField>
                <FormField label="Email" required error={errors.email} hint={<Mail className="w-3.5 h-3.5" />}>
                  <Input
                    type="email"
                    name="email"
                    value={this.state.email}
                    onChange={this.handleChange}
                    placeholder="vous@exemple.fr"
                    required
                  />
                </FormField>
                <FormField label="Rôle" required hint={<Shield className="w-3.5 h-3.5" />}>
                  <Select
                    value={this.state.role}
                    onChange={(e) => this.setState({ role: e.target.value, errors: {} })}
                    options={[
                      { value: "USER",  label: "Utilisateur" },
                      { value: "ADMIN", label: "Administrateur" },
                    ]}
                    required
                  />
                </FormField>
              </div>
            </Card>

            <Card>
              <Card.Header>
                <Card.Title>Mot de passe</Card.Title>
                {isEdit && <Card.Description>Laisse vide pour ne pas le changer.</Card.Description>}
              </Card.Header>
              <div className="space-y-4">
                <FormField
                  label={isEdit ? "Nouveau mot de passe" : "Mot de passe"}
                  required={!isEdit}
                  error={errors.password}
                  hint={<Lock className="w-3.5 h-3.5" />}
                >
                  <Input
                    type="password"
                    name="password"
                    value={this.state.password}
                    onChange={this.handleChange}
                    placeholder="••••••••"
                    required={!isEdit}
                    minLength={6}
                  />
                </FormField>
                <FormField
                  label="Confirmation"
                  required={!isEdit}
                  error={errors.confirmPassword}
                  hint={<Lock className="w-3.5 h-3.5" />}
                >
                  <Input
                    type="password"
                    name="confirmPassword"
                    value={this.state.confirmPassword}
                    onChange={this.handleChange}
                    placeholder="••••••••"
                    required={!isEdit}
                  />
                </FormField>
              </div>
            </Card>

            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => this.props.history.push("/dashboard/users")}
              >
                Annuler
              </Button>
              <Button type="submit" isLoading={saving}>
                {isEdit ? "Enregistrer" : "Créer l'utilisateur"}
              </Button>
            </div>
          </div>
        </form>
      </AdminPageShell>
    );
  }
}

export default withRouter(UserForm);
